import apiClient from '../../../../api/axios';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  TAllergensResponse,
  TAllergenResponse,
  TCreateAllergenRequest,
  TUpdateAllergenRequest,
} from '../schemas';

const ALLERGENS_BASE_URL = '/menu-allergens';
const QUERY_KEY = 'allergens';

export const useGetAllergens = (restaurantId: string) => {
  return useQuery({
    queryKey: [QUERY_KEY, restaurantId],
    queryFn: async () => {
      const response = await apiClient.get<TAllergensResponse>(ALLERGENS_BASE_URL, {
        headers: {
          'Restaurant-ID': restaurantId,
        },
      });
      return response.data?.data?.rows || [];
    },
    enabled: !!restaurantId,
  });
};

export const useGetAllergen = (id: string) => {
  return useQuery({
    queryKey: [QUERY_KEY, id],
    queryFn: async () => {
      const response = await apiClient.get<TAllergenResponse>(`${ALLERGENS_BASE_URL}/${id}`);
      return response.data?.data;
    },
    enabled: !!id,
  });
};

export const useCreateAllergen = (restaurantId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: TCreateAllergenRequest) => {
      const response = await apiClient.post<TAllergenResponse>(ALLERGENS_BASE_URL, payload, {
        headers: {
          'Restaurant-ID': restaurantId,
        },
      });
      return response.data?.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
};

export const useUpdateAllergen = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: TUpdateAllergenRequest }) => {
      const response = await apiClient.put<TAllergenResponse>(`${ALLERGENS_BASE_URL}/${id}`, payload);
      return response.data?.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
};

export const useDeleteAllergen = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await apiClient.delete(`${ALLERGENS_BASE_URL}/${id}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
};

type TUploadImageResponse = {
  success: boolean;
  data?: {
    fileId: string;
    location: string;
  };
  msg?: string;
};

export const useUploadImage = () => {
  return useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append('image', file);
      const response = await apiClient.post<TUploadImageResponse>('/files/upload-image', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    },
  });
};

export const allergensService = {
  useGetAllergens,
  useGetAllergen,
  useCreateAllergen,
  useUpdateAllergen,
  useDeleteAllergen,
  useUploadImage,
};
